import { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent,
} from '@mui/material';
import { Visibility as ViewIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { assistantsApiService } from '../../services/assistantsApiService';
import { sessionService } from '../../services/sessionService';
import DataTable, { type DataTableColumn } from '../data-display/DataTable';
import { SessionStatus, MessageRole } from '../../types/session';
import type { AgentSessionDto, AgentMessageDto } from '../../types/session';

const statusLabels: Record<number, string> = {
  [SessionStatus.Active]: 'Active',
  [SessionStatus.Paused]: 'Paused',
  [SessionStatus.Completed]: 'Completed',
  [SessionStatus.Cancelled]: 'Cancelled',
  [SessionStatus.Expired]: 'Expired',
  [SessionStatus.Error]: 'Error',
};

const roleLabels: Record<number, string> = {
  [MessageRole.System]: 'System',
  [MessageRole.User]: 'User',
  [MessageRole.Assistant]: 'Assistant',
  [MessageRole.Tool]: 'Tool',
};

interface Props {
  agentId: string;
  agentSlug: string;
}

export const ThreadDetail = ({ agentId, agentSlug }: Props) => {
  const [threads, setThreads] = useState<AgentSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState<AgentSessionDto | null>(null);
  const [messages, setMessages] = useState<AgentMessageDto[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);

  useEffect(() => {
    loadThreads();
  }, [agentId]);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const result = await sessionService.getSessions({
        agentDefinitionId: agentId,
        pageSize: 100,
        searchTerm: 'Thread',
      });
      setSessions(result.items ?? []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const setSessions = (items: AgentSessionDto[]) => {
    setThreads(items);
  };

  const viewMessages = async (thread: AgentSessionDto) => {
    setSelectedThread(thread);
    setMsgLoading(true);
    try {
      const msgs = await sessionService.getMessages(thread.id);
      setMessages(msgs);
    } catch {
      setMessages([]);
    } finally {
      setMsgLoading(false);
    }
  };

  const handleCreateThread = async () => {
    try {
      await assistantsApiService.createThread(agentSlug);
      loadThreads();
    } catch (err) {
      console.error('Failed to create thread', err);
    }
  };

  const handleDeleteThread = async (threadId: string) => {
    try {
      await sessionService.deleteSession(threadId);
      loadThreads();
    } catch (err) {
      console.error('Failed to delete thread', err);
    }
  };

  const columns: DataTableColumn<AgentSessionDto>[] = [
    {
      id: 'id', label: 'Thread ID',
      render: (r) => <Typography variant="body2" fontFamily="monospace" fontSize={12}>thread_{r.id.replace(/-/g, '')}</Typography>,
    },
    { id: 'sessionName', label: 'Name', render: (r) => <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>{r.sessionName}</Typography> },
    { id: 'messageCount', label: 'Messages', render: (r) => r.messageCount ?? 0 },
    {
      id: 'status', label: 'Status',
      render: (r) => <Chip label={statusLabels[r.status] ?? r.status} size="small" color={r.status === SessionStatus.Active ? 'success' : 'default'} variant="outlined" />,
    },
    {
      id: 'createdAt', label: 'Created',
      render: (r) => new Date(r.createdAt).toLocaleString(),
    },
    {
      id: 'actions', label: '', align: 'right',
      render: (r) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View messages">
            <IconButton size="small" onClick={() => viewMessages(r)}><ViewIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDeleteThread(r.id)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Threads (mapped to sessions) for this agent.
        </Typography>
        <Button variant="contained" size="small" onClick={handleCreateThread}>
          New Thread
        </Button>
      </Box>

      <DataTable columns={columns} rows={threads} loading={loading} emptyMessage="No threads found." />

      <Dialog open={!!selectedThread} onClose={() => setSelectedThread(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Thread Messages — {selectedThread?.sessionName}
        </DialogTitle>
        <DialogContent>
          {msgLoading ? (
            <Typography>Loading...</Typography>
          ) : messages.length === 0 ? (
            <Typography color="text.secondary">No messages in this thread.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {messages.map((m) => (
                <Box
                  key={m.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: m.role === MessageRole.Assistant
                      ? 'var(--glass-bg-subtle)'
                      : m.role === MessageRole.Tool
                        ? 'rgba(255, 152, 0, 0.05)'
                        : 'transparent',
                    border: '1px solid var(--glass-border)',
                  }}
                >
                  <Typography variant="caption" color="primary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                    {roleLabels[m.role] ?? m.role}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {m.content}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
